import * as React from "react";
import { ClipboardList, Eye, Folder, History, Network, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToggle } from "react-use";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateBusinessReportModal } from "@/modules/snapshots/modals/createModal";
import { BusinessReportType } from "@/modules/snapshots/businessReports.types";
import { CommonTabs } from "@/components/app/tabs";
import { CommonTabsItem } from "@/components/app/tabs/types";
import { useTemplateLibraryAccess } from "@/hooks/use-template-library-access";
import { buildContractFamiliesPath } from "@/views/contract-families/navigation";
import { useSearchParams } from "react-router";
import { useContractFamiliesAccess } from "@/hooks/use-contract-families-access";

interface Props {
  selectedTab: string;
  children?: React.ReactNode;
}

const tabs: CommonTabsItem[] = [
  {
    name: "collections",
    label: "Collections",
    link: "/snapshots/collections",
    icon: (
      <Folder className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
    ),
  },
  {
    name: "templates",
    label: "Templates",
    link: "/snapshots/templates",
    icon: (
      <PlayCircle className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
    ),
  },
  {
    name: "recent",
    label: "Recent Snapshots",
    link: "/snapshots/recent",
    icon: (
      <History className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
    ),
  },
  {
    name: "views",
    label: "Views",
    link: "/snapshots/views",
    icon: (
      <Eye className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
    ),
  },
  {
    name: "recent-reports",
    label: "Recent Reports",
    link: "/snapshots/recent-reports",
    icon: (
      <ClipboardList className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
    ),
  },
  {
    name: "contract-families",
    label: "Contract Families",
    link: "/contract-families",
    icon: (
      <Network className="mr-2 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-data-[state=active]:text-desert-sand-700" />
    ),
  },
];

export const BusinessReportsTabs: React.FC<Props> = ({ selectedTab }) => {
  const [isCreateTemplateOpen, toggleIsCreateTemplateOpen] = useToggle(false);
  const [createType, setCreateType] = React.useState<BusinessReportType>("snapshot_template");
  const [searchParams] = useSearchParams();
  const { isAdmin, canAuthor } = useTemplateLibraryAccess();
  const { hasContractFamilies } = useContractFamiliesAccess();
  const snapshotCollectionId = searchParams.get("snapshotCollectionId");
  const contextualTabs = React.useMemo(
    () =>
      tabs
        .filter(tab => tab.name !== "contract-families" || hasContractFamilies)
        .map(tab =>
          tab.name === "contract-families"
            ? { ...tab, link: buildContractFamiliesPath(null, { snapshotCollectionId }) }
            : tab,
        ),
    [hasContractFamilies, snapshotCollectionId],
  );

  const openCreateTemplateModal = (type: BusinessReportType) => {
    setCreateType(type);
    toggleIsCreateTemplateOpen(true);
  };

  return (
    <CommonTabs tabs={contextualTabs}>
      {selectedTab === "templates" && canAuthor && (
        <div className="ml-auto flex h-full items-center">
          {isCreateTemplateOpen && (
            <CreateBusinessReportModal
              isOpen={isCreateTemplateOpen}
              setIsOpen={toggleIsCreateTemplateOpen}
              type={createType}
            />
          )}

          {!isAdmin && (
            <Button
              variant="default"
              size="sm"
              className="h-10 rounded-md bg-[#07142d] px-4 text-sm font-semibold text-white shadow-none hover:bg-[#111b35]"
              onClick={() => openCreateTemplateModal("authoring_template")}
            >
              New authoring template
            </Button>
          )}

          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  className="h-10 rounded-md bg-[#07142d] px-4 text-sm font-semibold text-white shadow-none hover:bg-[#111b35]"
                >
                  Upload Template
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => openCreateTemplateModal("snapshot_template")}>
                  SnapShot Template
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => openCreateTemplateModal("business_report")}>
                  Business Report
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => openCreateTemplateModal("authoring_template")}>
                  Authoring Template
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </CommonTabs>
  );
};
